import styled from 'styled-components';

import AutoKit from '../../../assets/images/pluvaAutoLanding/auto_kit.png';
import Controller from '../../../assets/images/pluvaAutoLanding/kit_controller.png';
import Devices from '../../../assets/images/pluvaAutoLanding/kit_devices.png';
import InterfaceBox from '../../../assets/images/pluvaAutoLanding/kit_interface-box.png';
import Remote from '../../../assets/images/pluvaAutoLanding/kit_remote.png';
import Steer from '../../../assets/images/pluvaAutoLanding/kit_steer.png';
import Steer2 from '../../../assets/images/pluvaAutoLanding/kit_steer2.png';
import { Icon } from '../../../components/common/icon/Icon';
import { PointBadge } from './PointBadge';

interface IKitItem {
  name: string;
  images: string[];
  description: string;
  eventBadgeTitle?: string;
  size: number;
  showAdditionalDescBox?: boolean;
}

const installationPart: IKitItem[] = [
  {
    name: '메인 컨트롤러',
    images: [Controller],
    size: 150,
    description: 'RTK-GPS(정밀 측정 기술)을 활용하여 실시간 위치 정보를 파악하고, 자율주행 경로를 생성합니다.',
  },
  {
    name: '오토스티어',
    images: [Steer, Steer2],
    size: 120,
    description: '기존 농기계의 핸들을 교체하거나 부착하여 메인컨트롤러가 생성한 경로를 따라 운전을 대신합니다.',
  },
  {
    name: '인터페이스 박스',
    images: [InterfaceBox],
    size: 150,
    description: '농기계 내부에 부착하여 농기계와 플루바오토 간의 전원 및 외부 단자 등을 연결할 수 있는 장비 입니다.',
  },
];

const manipulationUnit: IKitItem[] = [
  {
    name: '원터치 스위치',
    images: [Remote],
    size: 150,
    description: '운전석 근처에 부착하여 단 4번의 터치로 자율주행을 시작하거나 멈출 수 있도록 합니다.',
  },
  {
    name: '핸드폰이나 태블릿 PC',
    images: [Devices],
    size: 150,
    description:
      '플루바 오토 어플리케이션을 통하여 상세조작이 가능하며, 위성지도를 이용해 야간이나 물논 작업도 수월합니다.',
    eventBadgeTitle: '태블릿 증정 이벤트 중',
    showAdditionalDescBox: true,
  },
];

const KitItemBox = ({ item }: { item: IKitItem }) => {
  return (
    <KitBox>
      <KitImageWrap>
        {item.images.map((img) => {
          return <KitImage src={img} size={item.size} />;
        })}
        {item.eventBadgeTitle ? (
          <KitBadge>
            <KitBadgeText>{item.eventBadgeTitle}</KitBadgeText>
          </KitBadge>
        ) : null}
      </KitImageWrap>
      <KitDescription>
        <KitName>{item.name}</KitName>
        <KitDescriptionText>{item.description}</KitDescriptionText>
      </KitDescription>
      {item.showAdditionalDescBox ? (
        <KitAdditionalDescription>
          <AdditionalDescriptionText>
            {'플루바 오토 어플리케이션은\n'}
            <TextHighlight>{'애플과 안드로이드 모두 지원!'}</TextHighlight>
          </AdditionalDescriptionText>
          <AdditionalDescriptionIcons>
            <AdditionalDescriptionIconBox>
              <Icon name={'AppleGray'} width={15} height={18} />
            </AdditionalDescriptionIconBox>
            <AdditionalDescriptionIconBox>
              <Icon name={'Android'} width={17} height={20} />
            </AdditionalDescriptionIconBox>
          </AdditionalDescriptionIcons>
        </KitAdditionalDescription>
      ) : null}
    </KitBox>
  );
};

export const KitDetailsSection = () => {
  return (
    <Container>
      <TitleSection>
        <PointBadge title={'제품 소개'} />
        <Title>{'키트 설치는 1시간,\n기능 업데이트는 무료!'}</Title>
        <KitItemsImage src={AutoKit} />
        <TitleHelperText>{'타공 없이 1시간이면 내 트랙터에\n자율 주행 키트 설치 완료'}</TitleHelperText>
      </TitleSection>
      <ContentSection>
        <SubTitle>{'자율 주행을 위해 트랙터에\n이런 제품들이 설치돼요'}</SubTitle>
        <KitItemsContainer>
          {installationPart.map((item) => {
            return <KitItemBox item={item} />;
          })}
        </KitItemsContainer>
        <SubTitleSection>
          <SubTitle>{'자율 주행시엔\n이런 장치로 조작해요'}</SubTitle>
          <TitleHelperText>{'리모콘이나 핸드폰 또는\n태블릿으로 간편 조작 가능'}</TitleHelperText>
        </SubTitleSection>
        <KitItemsContainer>
          {manipulationUnit.map((item) => {
            return <KitItemBox item={item} />;
          })}
        </KitItemsContainer>
      </ContentSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 0px 24px 48px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Title = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  white-space: pre-line;
`;

const TitleHelperText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  white-space: pre-line;
`;

const SubTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const SubTitle = styled.h3`
  color: var(--Light-Scheme-Color-primary-green, #34c77e);
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 26px */

  white-space: pre-line;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const KitItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const KitItemsImage = styled.img`
  width: 200px;
  height: 200px;
`;

const KitBox = styled.div`
  flex-direction: column;
  gap: var(--radius-large, 16px);

  display: flex;
  padding: 24px;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const KitDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const KitName = styled.h4`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const KitDescriptionText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const KitImageWrap = styled.div`
  position: relative;
  display: flex;
  min-height: 180px;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const KitImage = styled.img<{ size: number }>`
  width: ${(props) => props.size}px;
`;

const KitBadge = styled.div`
  position: absolute;
  left: 10px;
  top: 11px;

  display: flex;
  height: 28px;
  padding: 8px;

  border-radius: 8px;
  background: var(--Light-Scheme-Color-primary-green, #34c77e);

  align-items: center;
  gap: 4px;
`;

const KitBadgeText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-align: center;

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const KitAdditionalDescription = styled.div`
  display: flex;
  padding: var(--radius-medium, 8px) var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-white, #fff);

  @media screen and (max-width: 340px) {
    display: flex;
    padding: var(--radius-large, 16px) var(--radius-medium, 8px);
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--radius-medium, 8px);
    align-self: stretch;
  }
`;

const AdditionalDescriptionText = styled.p`
  flex: 1;
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 15.6px */
  white-space: pre-line;
`;

const TextHighlight = styled.span`
  color: var(--Light-Scheme-Color-primary-green, #34c77e);
`;

const AdditionalDescriptionIcons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--radius-medium, 8px);
`;

const AdditionalDescriptionIconBox = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  padding-bottom: 3px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;
